import { Smartphone as SmartphoneIcon, ChevronRight as ChevronRightIcon } from 'lucide-react'
import { type DetectedDeviceResponse } from '../../lib/api'
import { getiPhoneModelName } from '../../lib/utils'
import { DeviceReadinessPanel } from '../DeviceReadinessPanel'
import { useHelperStatus } from '../../hooks/useHelperStatus'

interface SelectDeviceProps {
  devices: DetectedDeviceResponse[]
  isLoading: boolean
  onSelect: (device: DetectedDeviceResponse) => void
  /** Filter devices by platform */
  platform?: 'ios' | 'android'
}

function getDeviceModelDisplay(device: DetectedDeviceResponse): string {
  if (device.platform === 'android') {
    return device.model || 'Android Device'
  }
  return getiPhoneModelName(device.model)
}

export default function SelectDevice({ devices, onSelect, platform }: SelectDeviceProps) {
  const { data: helper } = useHelperStatus()
  const filteredDevices = platform
    ? devices.filter(d => (d.platform || 'ios') === platform)
    : devices

  const hasDevices = filteredDevices.length > 0
  const helperReady = helper?.running === true

  return (
    <div className="space-y-4">
      {(!helperReady || !hasDevices) && (
        <DeviceReadinessPanel
          platform={platform ?? null}
          deviceConnected={helperReady && hasDevices}
          mode="install"
          hideAction
        />
      )}

      {helperReady && filteredDevices.map((device) => (
        <button
          key={device.id}
          onClick={() => onSelect(device)}
          className="w-full bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-brand hover:shadow-md transition-all text-left"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center flex-shrink-0">
              <SmartphoneIcon size={24} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{device.name || 'Unknown Device'}</h3>
              <p className="text-sm text-gray-500">{getDeviceModelDisplay(device)}</p>
            </div>
            <ChevronRightIcon size={20} className="text-gray-400" />
          </div>
        </button>
      ))}
    </div>
  )
}
